import type { TFunction } from 'i18next';
import {
  type Account,
  FieldMode,
  I18nCountry,
  JapanPrefecture,
  type UserProfileForm
} from '@linktivity/ars-neo';
import type { Rule } from '@linktivity/link-ui';
import { DOMAIN_LIKE_REGEX, NAME_REGEX } from './regex';

/**
 * A profile field counts as required only when the site config explicitly
 * marks it required; unspecified or missing config leaves it optional.
 */
export const isProfileFieldRequired = (mode?: FieldMode): boolean =>
  mode === FieldMode.FieldModeRequired;

export const isProfileFieldVisible = (mode?: FieldMode): boolean =>
  mode !== FieldMode.FieldModeHidden;

/**
 * Returns true when the account has every field the site config marks as
 * required. Keep the required-ness semantics in sync with the ProfileForm
 * validation rules in packages/share/src/views/Profile/ProfileForm.tsx.
 * Presence-only by design: the form additionally enforces format rules
 * (name/email regexes), so the form is strictly stricter than this gate
 * and a redirect loop between them is impossible.
 */
export const isProfileComplete = (
  account: Pick<
    Account,
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'gender'
    | 'birthday'
    | 'country'
    | 'japanPrefecture'
  >,
  userProfileForm?: UserProfileForm
): boolean => {
  if (
    isProfileFieldRequired(userProfileForm?.name) &&
    (!account.firstName?.trim() || !account.lastName?.trim())
  ) {
    return false;
  }

  if (
    isProfileFieldRequired(userProfileForm?.email) &&
    !account.email?.trim()
  ) {
    return false;
  }

  if (isProfileFieldRequired(userProfileForm?.gender) && !account.gender) {
    return false;
  }

  if (isProfileFieldRequired(userProfileForm?.birthday) && !account.birthday) {
    return false;
  }

  if (isProfileFieldRequired(userProfileForm?.nationality)) {
    if (!account.country || account.country === I18nCountry.Universal) {
      return false;
    }

    if (account.country === I18nCountry.Jpn) {
      return (
        !!account.japanPrefecture &&
        account.japanPrefecture !== JapanPrefecture.Oversea
      );
    }
  }

  return true;
};

export const createNameRules = (
  t: TFunction,
  fieldLabel: string,
  required = true
): Rule[] => [
  {
    required,
    message: t('share.validation.required', { field: fieldLabel }),
    trigger: ['change', 'blur']
  },
  {
    validator: (_, value) => {
      if (typeof value !== 'string') {
        return true;
      }

      const trimmedValue = value.trim();

      if (trimmedValue.length === 0) {
        return !required;
      }

      return (
        NAME_REGEX.test(trimmedValue) && !DOMAIN_LIKE_REGEX.test(trimmedValue)
      );
    },
    message: t('share.validation.nameInvalid', { field: fieldLabel }),
    trigger: ['change', 'blur']
  }
];
